import { datadogRum } from '@datadog/browser-rum';
import { message } from 'antd';
import { isAxiosError } from 'axios';
import { t } from 'i18next';

export enum ErrorSeverity {
  /** Log to Datadog RUM only — no user-facing notification */
  INFO = 'info',
  /** Log to Datadog RUM + show a warning toast to the user */
  WARNING = 'warning',
  /** Log to Datadog RUM + show an error toast to the user */
  CRITICAL = 'critical',
}

interface ErrorServiceOptions {
  /** Where the error originated (e.g. component name, hook, util) */
  source: string;
  /** What the user/system was trying to do (e.g. "downloading PDF", "refreshing token") */
  operation?: string;
  /** Override the default toast message for this severity level */
  userMessage?: string;
  /** Extra structured context forwarded to Datadog RUM */
  context?: Record<string, unknown>;
}

/**
 * Centralized error handler for Portal Neo.
 *
 * Funnels all errors through Datadog RUM and, depending on severity,
 * shows a user-facing toast notification.
 *
 * Severity behaviour:
 *  - INFO:     Datadog RUM only (silent)
 *  - WARNING:  Datadog RUM + warning toast
 *  - CRITICAL: Datadog RUM + error toast
 */
export function handleError(
  error: unknown,
  severity: ErrorSeverity,
  options: ErrorServiceOptions
): void {
  const { source, operation, userMessage, context } = options;

  // 422 (Unprocessable Entity) errors are validation responses from the backend
  // and should not be reported as errors to Datadog RUM
  if (isAxiosError(error) && error.response?.status === 422) {
    return;
  }

  // Normalise the error into something Datadog can ingest
  let normalisedError: Error;
  if (error instanceof Error) {
    normalisedError = error;
  } else {
    normalisedError = new Error(typeof error === 'string' ? error : String(error));
  }

  // Build a rich context object for observability
  const rumContext: Record<string, unknown> = {
    severity,
    source,
    ...(operation && { operation }),
    ...context,
  };

  // Always report to Datadog RUM
  try {
    datadogRum.addError(normalisedError, rumContext);
  } catch {
    // If RUM isn't initialised (e.g. local dev), fall back to console
    console.error(`[${severity.toUpperCase()}] [${source}]`, normalisedError);
  }

  // User-facing notification based on severity
  switch (severity) {
    case ErrorSeverity.WARNING:
      message.warning(userMessage ?? t('wrong'));
      break;
    case ErrorSeverity.CRITICAL:
      message.error(userMessage ?? t('wrong'));
      break;
    case ErrorSeverity.INFO:
    // falls through
    default:
      // Silent — no user notification
      break;
  }
}

/**
 * Installs global handlers for uncaught errors and unhandled promise rejections.
 * Call once at app startup (e.g. in index.tsx).
 */
export function installGlobalErrorHandlers(): void {
  window.addEventListener('error', (event: ErrorEvent) => {
    handleError(event.error ?? event.message, ErrorSeverity.INFO, {
      source: 'window.onerror',
      context: {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      },
    });
  });

  window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
    handleError(event.reason, ErrorSeverity.INFO, {
      source: 'unhandledrejection',
    });
  });
}
